import * as _ from 'lodash';

import {extension as fileExtension} from '../utils/path';
import {hexToId} from '../utils/digest';
import {splitOnce} from '../utils/string';
import * as StringUtils from '../utils/string';

import * as MediaImage from './mediaImage';
import * as MediaTable from './mediaTable';
import * as MediaVideo from './mediaVideo';
import * as MediaAudio from './mediaAudio';
import * as MediaHTML from './mediaHTML';
import * as MediaBokeh from './mediaBokeh';
import * as MediaObject3D from './mediaObject3D';
import * as MediaMolecule from './mediaMolecule';

/** Safety mechanism: if we encounter tag chains this deep, something has
 * gone wrong.
 */
export const TAG_DEPTH_LIMIT = 1000;

// All nodes have a type (using our run-time type system).
export interface BaseNode<T extends Type> {
  type: T;
}

// Ops always produce a single output-node.
export interface OutputNode<T extends Type = Type> extends BaseNode<T> {
  nodeType: 'output';
  fromOp: Op;

  // Server-side only
  id?: string;
}

// A variable node just has a name
export interface VarNode<T extends Type = Type> extends BaseNode<T> {
  nodeType: 'var';
  varName: string;
}

// A variable node stores its value
export interface ConstNode<T extends Type = Type> extends BaseNode<T> {
  nodeType: 'const';
  val: TypeToTSTypeInner<T>;
}

export type Node<T extends Type = Type> =
  | OutputNode<T>
  | ConstNode<T>
  | VarNode<T>;

// A void node represents nothing. It's just used to indicate the lack
// of any node in certain UI circumstances.
export interface VoidNode extends BaseNode<'invalid'> {
  nodeType: 'void';
}

export type NodeOrVoidNode<T extends Type = Type> = Node<T> | VoidNode;
export type OpInputs = {[key: string]: Node<any>};

export interface Op {
  name: string;
  inputs: OpInputs;
}

export function isOutputNode(node: NodeOrVoidNode): node is OutputNode {
  return node.nodeType === 'output';
}

export function asOutputNode(node: NodeOrVoidNode): OutputNode | undefined {
  return isOutputNode(node) ? node : undefined;
}

/*********************/
/* Weave Type System */
/*********************/

export type BasicType =
  | 'invalid'
  | 'any'
  | 'unknown'
  | 'string'
  | 'id'
  | 'number'
  | 'boolean'
  | 'date'
  | 'report'
  | 'artifact'
  | 'artifactMembership'
  | 'artifactVersion'
  | 'artifactType'
  | 'artifactAlias'
  | 'run'
  | 'entity'
  | 'project'
  | 'org'
  | 'user'
  | 'type' // #!
  | 'link'
  | 'none';

export type SimpleType = BasicType;

export const SORTABLE_TYPES: Type[] = [
  'id',
  'string',
  'number',
  'boolean',
  'none',
];

export const typesWithDigest: Type[] = [
  {type: 'image-file'},
  {type: 'video-file'},
  {type: 'audio-file'},
  {type: 'object3D-file'},
  {type: 'molecule-file'},
];

export const GROUPABLE_TYPES: Type[] = SORTABLE_TYPES.concat(
  SORTABLE_TYPES.map(t => ({type: 'dict', objectType: maybe(t)})),
  SORTABLE_TYPES.map(t => ({
    type: 'list',
    maxLength: 10,
    objectType: maybe(t),
  }))
).concat(typesWithDigest);

export function isSimpleType(t: Type): t is SimpleType {
  return typeof t === 'string';
}

// TODO: figure out how this merges with dict
export interface TypedDictType {
  type: 'typedDict';

  // undefined here forces us to handle cases where properties
  // missing
  propertyTypes: {[key: string]: Type | undefined};
}

export interface Dict<T extends Type = Type> {
  type: 'dict';
  objectType: T;
}

export interface TaggedValueType<T extends Type = Type, V extends Type = Type> {
  type: 'tagged';
  tag: T;
  value: V;
}

export interface ListType<T extends Type = Type> {
  type: 'list';
  objectType: T;
  // length?: number;
  minLength?: number;
  maxLength?: number;
}

// Used to denote support for any dimension of object.
// 0d: obj
// 1d: Array<obj>
// 2d: Array<Array<obj>>
// ...
// Note this is currently not used anywhere, but I'm keeping
// it because I like the idea (tensor is nested arrays)
// export interface Tensor {
//   type: 'tensor';
//   objectType: Type;
// }

export interface ImageType {
  type: 'image-file';
  boxLayers?: {[layerName: string]: string[]};
  boxScoreKeys?: string[];
  maskLayers?: {[layerName: string]: string[]};
  classMap?: {[key: string]: string};
}

export interface VideoType {
  type: 'video-file';
}

export interface AudioType {
  type: 'audio-file';
}

export interface HtmlType {
  type: 'html-file';
}

export interface BokehType {
  type: 'bokeh-file';
}

export interface Object3DType {
  type: 'object3D-file';
}

export interface MoleculeType {
  type: 'molecule-file';
}

export interface TableType {
  type: 'table';
  columnTypes: {[key: string]: Type};
}

export interface JoinedTableType {
  type: 'joined-table';
  columnTypes: {[key: string]: Type};
}

export interface PartitionedTableType {
  type: 'partitioned-table';
  columnTypes: {[key: string]: Type};
}

export type MediaType =
  | ImageType
  | VideoType
  | AudioType
  | HtmlType
  | BokehType
  | Object3DType
  | MoleculeType
  | TableType
  | JoinedTableType
  | PartitionedTableType;

export const BasicMediaTypes: MediaType[] = [
  {type: 'image-file'},
  {type: 'video-file'},
  {type: 'audio-file'},
  {type: 'html-file'},
  {type: 'bokeh-file'},
  {type: 'object3D-file'},
  {type: 'molecule-file'},
  {type: 'table', columnTypes: {}},
  {type: 'joined-table', columnTypes: {}},
  {type: 'partitioned-table', columnTypes: {}},
];

const isMediaType = (type: Type): type is MediaType => {
  return (
    !isSimpleType(type) &&
    BasicMediaTypes.filter(mt => type.type === mt.type) != null
  );
};

export const isMediaTypeLike = (type: Type): type is MediaType => {
  return BasicMediaTypes.some(t => isAssignableTo2(t, type));
};

interface FunctionInputTypes {
  [key: string]: Type;
}

export interface FunctionType {
  type: 'function';
  inputTypes: FunctionInputTypes;
  outputType: Type;
}

export interface FunctionTypeSpecific<
  InputTypes extends FunctionInputTypes,
  ReturnType extends Type
> {
  type: 'function';
  inputTypes: InputTypes;
  outputType: ReturnType;
}

export interface File {
  type: 'file';
  extension?: string;
  wbObjectType?: MediaType | 'none';
}

export interface Dir {
  type: 'dir';
}

export interface Union {
  type: 'union';
  members: Type[];
}

export interface NDArrayType {
  type: 'ndarray';
  serializationPath: {
    key: string;
    path: string;
  };
  shape: number[];
}

export interface TimestampType {
  type: 'timestamp';
  // TODO: support additional units in the future.
  unit: 'ms';
}

export type QueryPath = ObjectId;

export interface Link {
  name: string;
  url: string;
}

export type ComplexType =
  | TypedDictType
  | ListType
  // | Tensor
  | Dict
  | TaggedValueType
  | TableType
  | JoinedTableType
  | PartitionedTableType
  | MediaType
  | Dir
  | File
  | FunctionType
  | Union
  | NDArrayType
  | TimestampType;
export type Type = ComplexType | SimpleType;

export type TypeID = ComplexType['type'] | SimpleType;

// Convert a Type.Type to an actual Typescript type that matches it!
export type TypeToTSTypeInner<T> = T extends 'any'
  ? any
  : T extends 'user'
  ? any // TODO
  : T extends 'project'
  ? any // TODO
  : T extends 'entity'
  ? any // TODO
  : T extends 'run'
  ? any // TODO
  : T extends 'artifactType'
  ? any // TODO
  : T extends 'artifact'
  ? any // TODO
  : T extends 'artifactVersion'
  ? any // TODO
  : T extends 'artifactAlias'
  ? any
  : T extends 'artifactMembership'
  ? any // TODO
  : T extends 'filter'
  ? any // TODO
  : T extends 'link'
  ? Link
  : T extends 'id'
  ? string
  : T extends 'string'
  ? string
  : T extends 'number'
  ? number
  : T extends 'boolean'
  ? boolean
  : T extends 'none'
  ? null
  : T extends 'date'
  ? Date
  : T extends 'unknown'
  ? unknown
  : T extends 'type'
  ? Type
  : T extends FunctionTypeSpecific<infer IT, infer RT>
  ? Node<RT> | VoidNode
  : T extends FunctionType
  ? Node<T['outputType']>
  : T extends ImageType
  ? MediaImage.WBImage
  : T extends VideoType
  ? MediaVideo.WBVideo
  : T extends AudioType
  ? MediaAudio.WBAudio
  : T extends HtmlType
  ? MediaHTML.WBHtml
  : T extends BokehType
  ? MediaBokeh.WBBokeh
  : T extends Object3DType
  ? MediaObject3D.Object3D
  : T extends MoleculeType
  ? MediaMolecule.Molecule
  : T extends TableType
  ? MediaTable.MediaTable
  : T extends File
  ? {artifact?: {id: string}; path: string}
  : T extends Dir
  ? DirMetadata
  : T extends TimestampType
  ? number
  : T extends Dict
  ? DictTSType<T['objectType']>
  : T extends ListType
  ? ListTsType<T['objectType']>
  : T extends Union // Use a mapped type to distribute TypeToTSType over the members array, // then use [number] lookup on the array to get a union, which produces // a union of the array's members. (this basically says "give me the type // that would result from indexing the array with any number").
  ? {[K in keyof T['members']]: TypeToTSTypeInner<T['members'][K]>}[number]
  : never;

type DictTSType<T> = {[key: string]: TypeToTSTypeInner<T>};

type ListTsType<T> = Array<TypeToTSTypeInner<T>>;

export function toString(
  type: Type,
  simple: boolean = true,
  indent: number = 0
): string {
  if (type === undefined) {
    return 'INVALID_TYPE_ERROR';
  }
  if (isSimpleType(type)) {
    return type;
  } else if (type.type === 'typedDict') {
    return (
      '{\n' +
      _.map(type.propertyTypes, (v, k) =>
        StringUtils.indent(
          k + ':' + toString(v!, simple, indent + 1) + ',\n',
          indent + 1
        )
      ).join('') +
      StringUtils.indent('}', indent)
    );
  } else if (type.type === 'table') {
    return (
      'Table<[\n' +
      _.map(type.columnTypes, (v, k) =>
        StringUtils.indent(
          k + ':' + toString(v!, simple, indent + 1) + ',\n',
          indent + 1
        )
      ).join('') +
      StringUtils.indent(']>', indent)
    );
  } else if (type.type === 'joined-table') {
    return (
      'JoinedTable<[\n' +
      _.map(type.columnTypes, (v, k) =>
        StringUtils.indent(
          k + ':' + toString(v!, simple, indent + 1) + ',\n',
          indent + 1
        )
      ).join('') +
      StringUtils.indent(']>', indent)
    );
  } else if (type.type === 'partitioned-table') {
    return (
      'PartitionedTable<[\n' +
      _.map(type.columnTypes, (v, k) =>
        StringUtils.indent(
          k + ':' + toString(v!, simple, indent + 1) + ',\n',
          indent + 1
        )
      ).join('') +
      StringUtils.indent(']>', indent)
    );
  } else if (type.type === 'dict') {
    return `{[k]: ${toString(type.objectType, simple, indent + 1)}}`;
  } else if (type.type === 'tagged') {
    if (simple) {
      return toString(type.value, simple, indent);
    }
    return (
      'Tagged<\n' +
      StringUtils.indent(
        toString(type.tag, simple, indent + 1) + ',\n',
        indent + 1
      ) +
      StringUtils.indent(
        toString(type.value, simple, indent + 1) + ',\n',
        indent + 1
      ) +
      StringUtils.indent('>', indent)
    );
  } else if (type.type === 'list') {
    return `List<${toString(type.objectType, simple, indent)}>`;
  } else if (type.type === 'function') {
    return `Function<outputType: ${toString(type.outputType, simple, indent)}>`;
  } else if (type.type === 'file') {
    if (type.wbObjectType != null) {
      return `File<${toString(type.wbObjectType, simple, indent)}>`;
    }
    return `File<{${
      type.extension != null ? 'extension: ' + type.extension : ''
    }}>`;
  } else if (type.type === 'dir') {
    return 'Dir';
  } else if (type.type === 'union') {
    return (
      'Union<[\n' +
      type.members
        .map(t =>
          StringUtils.indent(
            '| ' + toString(t, simple, indent + 1) + '\n',
            indent + 1
          )
        )
        .join('') +
      StringUtils.indent(']>', indent)
    );
  } else if (type.type === 'ndarray') {
    return `NDArray<${type.shape}>`;
  } else if (isMediaType(type)) {
    return type.type;
  }
  throw new Error(`unhandled type ${JSON.stringify(type)}`);
}

// TODO define when toTag is tagged value
// Can assign a tagged value as long as the target tag is possibly present
// somewhere in the lhs tag chain
export function tagAssignable(tagType: Type, toTag: Type): boolean {
  if (isTaggedValue(tagType)) {
    return (
      isAssignableTo2(tagType.value, toTag) || tagAssignable(tagType.tag, toTag)
    );
  } else if (isTaggedValueUnion(tagType)) {
    return tagType.members.some(m => tagAssignable(m, toTag));
  } else {
    return isAssignableTo2(tagType, toTag);
  }
}

export function isAssignableTo(type: Type, toType: Type): boolean {
  return isAssignableTo2(type, toType);
}

export function isAssignableTo2(type: Type, toType: Type): boolean {
  // console.log(
  //   'IS ASSIGN 2\n',
  //   toString(type, false),
  //   '\n -> \n',
  //   toString(toType, false)
  // );
  // even void is assignable to any!
  if (toType === 'any') {
    return true;
  }
  if (type === 'invalid') {
    return false;
  }
  if (isTaggedValue(type) && isAssignableTo2(type.value, toType)) {
    return true;
  } else if (isUnion(type)) {
    // Union to union assignment. Each member in type must be assignable to
    // toType.
    return type.members.every(typeMemberType =>
      isAssignableTo2(typeMemberType, toType)
    );
  } else if (isUnion(toType)) {
    return toType.members.some(fitMemberType =>
      isAssignableTo2(type, fitMemberType)
    );
  } else if (isTaggedValue(type)) {
    // A tag value can be assigned to a tag value if the value itself is assignable,
    // and if there is any tag in the tag chain of type that's assignable to toType's
    // tag.
    if (isTaggedValue(toType) && isAssignableTo2(type.value, toType.value)) {
      if (isTaggedValueLike(toType.tag)) {
        // If we're assigning to a tagged value whose tag is tagged value,
        // just check assignability of our tag to their tag.
        // This case is not used by any ops currently, but the union() function
        // in this file checks for type equality by doing a->b && b->a which
        // triggers this path.
        return isAssignableTo2(toType.tag, type.tag);
      } else {
        // Use our special tagAssignable check, which says we can assign a taggedvalue
        // as long as the tag is possibly present somewhere in the tag chain.
        return tagAssignable(type.tag, toType.tag);
      }
    }
  }
  if (isSimpleType(type) && isSimpleType(toType)) {
    return type === toType;
  } else if (!isSimpleType(type) && !isSimpleType(toType)) {
    if (type.type === 'dict' && toType.type === 'dict') {
      return isAssignableTo2(type.objectType, toType.objectType);
    } else if (type.type === 'dict' && toType.type === 'typedDict') {
      for (const propType of Object.values(toType.propertyTypes)) {
        if (!isAssignableTo2(type.objectType, propType!)) {
          return false;
        }
      }
      return true;
    } else if (type.type === 'table' && toType.type === 'table') {
      // TODO: not true! Check column types!
      return true;
    } else if (type.type === 'joined-table' && toType.type === 'joined-table') {
      // TODO: not true! Check column types!
      return true;
    } else if (
      type.type === 'partitioned-table' &&
      toType.type === 'partitioned-table'
    ) {
      // TODO: not true! Check column types!
      return true;
    } else if (type.type === 'typedDict' && toType.type === 'typedDict') {
      // Must have all keys in toType and types must match
      for (const key of Object.keys(toType.propertyTypes)) {
        const toKeyType = toType.propertyTypes[key]!;
        const keyType = type.propertyTypes[key];
        if (keyType === undefined || !isAssignableTo2(keyType, toKeyType)) {
          return false;
        }
      }
      return true;
    } else if (type.type === 'typedDict' && toType.type === 'dict') {
      // Must have all keys in toType and types must match
      const properties = Object.keys(type.propertyTypes);
      if (properties.length === 0) {
        // Must have at least one key to match. This is kind of odd,
        // we can revisit later if we need. This prevents us from creating
        // panels like PanelIdCompare when we have an empty {}, like in the
        // return type of opTableRows.
        return false;
      }
      for (const key of properties) {
        const keyType = type.propertyTypes[key];
        if (!isAssignableTo2(keyType!, toType.objectType)) {
          return false;
        }
      }
      return true;
    } else if (type.type === 'list' && toType.type === 'list') {
      if (
        toType.maxLength != null &&
        (type.maxLength == null || type.maxLength > toType.maxLength)
      ) {
        return false;
      } else if (
        toType.minLength != null &&
        (type.minLength ?? 0) < toType.minLength
      ) {
        return false;
      }
      return isAssignableTo2(type.objectType, toType.objectType);
    } else if (type.type === 'file' && toType.type === 'file') {
      if (
        toType.wbObjectType != null &&
        !isAssignableTo2(type.wbObjectType ?? 'none', toType.wbObjectType)
      ) {
        return false;
      }
      if (toType.extension != null && type.extension !== toType.extension) {
        return false;
      }
      return true;
    } else if (type.type === 'dir' && toType.type === 'dir') {
      return true;
    } else if (type.type === 'function' && toType.type === 'function') {
      return isAssignableTo2(type.outputType, toType.outputType);
    } else if (type.type === 'ndarray' && toType.type === 'ndarray') {
      // TODO: not true! Check shape!
      return true;
    } else if (type.type === 'image-file' && toType.type === 'image-file') {
      return true;
    } else if (type.type === 'video-file' && toType.type === 'video-file') {
      return true;
    } else if (type.type === 'audio-file' && toType.type === 'audio-file') {
      return true;
    } else if (type.type === 'html-file' && toType.type === 'html-file') {
      return true;
    } else if (type.type === 'bokeh-file' && toType.type === 'bokeh-file') {
      return true;
    } else if (
      type.type === 'object3D-file' &&
      toType.type === 'object3D-file'
    ) {
      return true;
    } else if (
      type.type === 'molecule-file' &&
      toType.type === 'molecule-file'
    ) {
      return true;
    } else if (type.type === 'timestamp' && toType.type === 'timestamp') {
      return true;
    }
  }
  // if (isTensor(toType)) {
  //   while (!isSimpleType(type) && type.type === 'list') {
  //     if (isAssignableTo2(type, toType.objectType)) {
  //       return true;
  //     }
  //     return isAssignableTo2(type.objectType, toType.objectType);
  //   }
  //   return isAssignableTo2(type, toType.objectType);
  // }
  return false;
}

// Implements linear tag-chaining in the same way makeTaggingStandardOp does.
export function withNamedTag(tagName: string, tag: Type, value: Type): Type {
  if (isTaggedValueLike(tag)) {
    return taggedValue(
      taggedValueTagType(tag),
      taggedValue(typedDict({[tagName]: taggedValueValueType(tag)}), value)
    );
  }
  return taggedValue(typedDict({[tagName]: tag}), value);
}

export function concreteWithNamedTag(
  tagName: string,
  tag: any,
  value: any
): any {
  if (isConcreteTaggedValue(tag)) {
    return concreteTaggedValue(
      tag._tag,
      concreteTaggedValue({[tagName]: tag._value}, value)
    );
  }
  return concreteTaggedValue({[tagName]: tag}, value);
}

export function withGroupTag(base: Type, keyType: Type): Type {
  return withNamedTag('groupKey', keyType, base);
}
export function withJoinTag(base: Type, keyType: Type): Type {
  return taggedValue(typedDict({joinKey: 'string', joinObj: keyType}), base);
}

export function withTableRowTag(base: Type, tableType: Type): Type {
  return withNamedTag('table', tableType, base);
}

export function withFileTag(base: Type, fileType: Type): Type {
  return taggedValue(typedDict({file: fileType}), base);
}

export function fileTable(): Type {
  return withFileTag(
    {type: 'table', columnTypes: {}},
    file('json', {type: 'table', columnTypes: {}})
  );
}

export function tableRowValue(valueType: Type): Type {
  return withTableRowTag(
    valueType,
    withFileTag(
      maybe({type: 'table', columnTypes: {}}),
      file('json', {type: 'table', columnTypes: {}})
    )
  );
}

export function nDims(t: Type): number {
  t = nullableTaggableValue(t);
  if (isListLike(t)) {
    return 1 + nDims(listObjectType(t));
  }
  return 0;
}

// This is called a lot at the top level of modules, so we don't
// use union(), which relies on isAssignableTo. Doing this speeds up the
// unit test startup time.
export function maybe(t: Type): Type {
  if (isNullable(t)) {
    return t;
  } else if (isUnion(t)) {
    return {type: 'union', members: ['none', ...t.members]};
  } else {
    return {type: 'union', members: ['none', t]};
  }
}

// This is called a lot at the top level of modules, so we don't
// use union(), which relies on isAssignableTo. Doing this speeds up the
// unit test startup time.
export function oneOrMany(objectType: Type): Type {
  return {type: 'union', members: [list(objectType), objectType]};
}

export function nullableOneOrMany(objType: Type): Type {
  return maybe(oneOrMany(maybe(objType)));
}

// If a type is a maybe, get the inner type.
export function nonNullable(t: Type): Type {
  if (isUnion(t)) {
    return union(t.members.filter(memberType => memberType !== 'none'));
  }
  return t;
}

// This should not skip tags. If we need a version that does that,
// make a new function called isNullableLike

export function isNullable(t: Type): boolean {
  return isUnion(t) && t.members.findIndex(m => m === 'none') !== -1;
}

export function union(members: Type[]): Type {
  if (members.length === 0) {
    return 'invalid';
  }
  let allMembers: Type[] = [];
  for (const mem of members) {
    if (!isSimpleType(mem) && mem.type === 'union') {
      allMembers = allMembers.concat(mem.members);
    } else if (isTaggedValue(mem) && isUnion(mem.value)) {
      allMembers = allMembers.concat(
        mem.value.members.map(m => taggedValue(mem.tag, m))
      );
    } else {
      allMembers.push(mem);
    }
  }
  const uniqMembers = _.uniqWith(
    allMembers,
    (a, b) =>
      _.isEqual(a, b) ||
      (a && b && isAssignableTo2(a, b) && isAssignableTo2(b, a))
  );

  // Split TaggedValue members out.
  const nonTaggedMembers: Type[] = [];
  const taggedMembers: TaggedValueType[] = [];
  for (const member of uniqMembers) {
    if (isTaggedValue(member)) {
      taggedMembers.push(member);
    } else {
      nonTaggedMembers.push(member);
    }
  }
  // Merge TaggedValues that have the same exact tags.
  const taggedMembersByTag = _.groupBy(taggedMembers, tv =>
    JSON.stringify(tv.tag)
  );
  const taggedMembersMerged = Object.values(taggedMembersByTag).map(
    sameTagMembers => {
      return {
        type: 'tagged' as const,
        tag: sameTagMembers[0].tag,
        value: union(sameTagMembers.map(m => m.value)),
      };
    }
  );

  const fullMembers = nonTaggedMembers.concat(taggedMembersMerged);
  if (fullMembers.length === 1) {
    return fullMembers[0];
  }
  return {type: 'union' as const, members: fullMembers};
}

export function isTaggedValue(t: Type): t is TaggedValueType {
  return !isSimpleType(t) && t.type === 'tagged';
}

export function isTaggedValueUnion(
  t: Type
): t is {type: 'union'; members: TaggedValueType[]} {
  return isUnion(t) && t.members.every(isTaggedValue);
}

export function isTaggedValueLike(t: Type): t is TaggedValueType | Union {
  if (isTaggedValue(t)) {
    return true;
  } else if (isTaggedValueUnion(t)) {
    return true;
  }
  return false;
}

export function taggedValueValueType(t: Type): Type {
  if (isTaggedValue(t)) {
    return t.value;
  } else if (isUnion(t)) {
    return union(t.members.map(m => (m as TaggedValueType).value));
  }
  throw new Error('invalid');
}

export function taggedValueTagType(t: Type): Type {
  if (isTaggedValue(t)) {
    return t.tag;
  } else if (isUnion(t)) {
    return union(t.members.map(m => (m as TaggedValueType).tag));
  }
  throw new Error('invalid');
}

// export function taggedValue(tagType: Type, valueType: Type): Type {
//   if (isTaggedValue(valueType)) {
//     // no tagged values allowed in the value-side! We ensure that recursive
//     // tags are always moved to the tag-side of a TaggedValue in opTypes.ts (taggable)
//     throw new Error('invalid');
//   }
//   return {
//     type: 'tagged',
//     tag: tagType,
//     value: valueType,
//   };
// }
export function taggedValue(
  tagType: Type | undefined | null,
  valueType: Type
): Type {
  if (tagType == null) {
    return valueType;
  }
  if (isUnion(valueType)) {
    return union(valueType.members.map(mem => taggedValue(tagType, mem)));
  }
  if (isTaggedValue(valueType)) {
    return taggedValue(taggedValue(tagType, valueType.tag), valueType.value);
  }
  return {
    type: 'tagged',
    tag: tagType,
    value: valueType,
  };
}

export interface ConcreteTaggedValue<Tag = any, Value = any> {
  _tag: Tag;
  _value: Value;
}
export type Val<Value, Tag = unknown> = Value | ConcreteTaggedValue<Tag, Value>;

/**
 * A type guard. Checks if given object x has the key.
 */
const has = <K extends string>(p: K, x: unknown): x is {[key in K]: unknown} =>
  typeof x === 'object' && x != null && p in x;

export function isConcreteTaggedValue<T, V>(
  value: unknown
): value is ConcreteTaggedValue<T, V> {
  return has('_tag', value) && has('_value', value);
}

export function concreteTaggedValue<Tag, ValueTag, ValueValue>(
  tag: Tag,
  value: ConcreteTaggedValue<ValueTag, ValueValue>
): ConcreteTaggedValue<ConcreteTaggedValue<Tag, ValueTag>, ValueValue>;
export function concreteTaggedValue<T, V>(
  tag: T,
  value: V
): ConcreteTaggedValue<T, V>;
export function concreteTaggedValue<T, V>(
  tag: T,
  value: V
): ConcreteTaggedValue<unknown, unknown> {
  if (isConcreteTaggedValue(value) && value._tag === tag) {
    return value;
  }
  if (isConcreteTaggedValue(value)) {
    return {
      _tag: concreteTaggedValue(tag, value._tag),
      _value: value._value,
    };
  }
  return {
    _tag: tag,
    _value: value,
  };
}

export function isUnion(t: Type): t is Union {
  return !isSimpleType(t) && t.type === 'union';
}

// export function isTensor(t: Type): t is Tensor {
//   return !isSimpleType(t) && t.type === 'tensor';
// }

// export function tensor(objectType: Type): Type {
//   return {
//     type: 'tensor',
//     objectType,
//   };
// }

export function isTypedDict(t: Type): t is TypedDictType {
  return !isSimpleType(t) && t.type === 'typedDict';
}

// TODO: Replace isTypedDict with isTypedDictLike in most places
export function isTypedDictLike(t: Type): t is TypedDictType | Union {
  return (
    isTypedDict(t) ||
    (isTaggedValue(t) && isTypedDictLike(t.value)) ||
    (isUnion(t) &&
      t.members.every(
        member =>
          isTypedDict(member) ||
          (isTaggedValue(member) && isTypedDict(member.value))
      ))
  );
}

export function typedDict(propertyTypes: {[key: string]: Type}): TypedDictType {
  return {
    type: 'typedDict',
    propertyTypes,
  };
}

export function isDict(t: Type): t is Dict {
  return !isSimpleType(t) && t.type === 'dict';
}

export function dict(objectType: Type): Type {
  return {
    type: 'dict',
    objectType,
  };
}

export function isList(t: Type): t is ListType {
  return !isSimpleType(t) && t.type === 'list';
}

// TODO: Replace isList with isListLike in most places
export function isListLike(t: Type): t is ListType | Union {
  return (
    isList(t) ||
    (isTaggedValue(t) && isList(t.value)) ||
    (isUnion(t) &&
      t.members.every(
        member =>
          isList(member) || (isTaggedValue(member) && isList(member.value))
      ))
  );
}

export function list(
  objectType: Type,
  minLength?: number,
  maxLength?: number
): ListType {
  if (minLength != null && maxLength != null) {
    return {
      type: 'list',
      objectType,
      minLength,
      maxLength,
    };
  } else if (minLength != null) {
    return {
      type: 'list',
      objectType,
      minLength,
    };
  } else if (maxLength != null) {
    return {
      type: 'list',
      objectType,
      maxLength,
    };
  }
  return {
    type: 'list',
    objectType,
  };
}

export function listWithLength(length: number, objectType: Type): ListType {
  return list(objectType, length, length);
}

export function isFile(t: Type): t is File {
  return !isSimpleType(t) && t.type === 'file';
}

export function isFileLike(t: Type): t is File {
  t = nullableTaggableValue(t);
  return isFile(t);
}

export function fileWbObjectType(t: Type): Type | undefined {
  t = nullableTaggableValue(t);
  if (!isFile(t)) {
    throw new Error('invalid');
  }
  return t.wbObjectType;
}

export function file(extension: string, wbObjectType: MediaType): Type {
  return {
    type: 'file',
    extension,
    wbObjectType,
  };
}

export function isFunction(t: Type): t is FunctionType {
  return !isSimpleType(t) && t.type === 'function';
}

export function isTable(t: Type): t is TableType {
  return !isSimpleType(t) && t.type === 'table';
}

export function isPartitionedTable(t: Type): t is TableType {
  return !isSimpleType(t) && t.type === 'partitioned-table';
}

export function isJoinedTable(t: Type): t is TableType {
  return !isSimpleType(t) && t.type === 'joined-table';
}

export function functionType(
  inputTypes: FunctionInputTypes,
  outputType: Type
): Type {
  return {type: 'function', inputTypes, outputType};
}

export const canSortType = (type?: Type): boolean => {
  if (type == null) {
    return false;
  }
  return isAssignableTo2(type, union(SORTABLE_TYPES));
};

export const canGroupType = (type?: Type): boolean => {
  if (type == null) {
    return false;
  }
  return isAssignableTo2(type, union(GROUPABLE_TYPES));
};

export const numberBin = typedDict({start: 'number', stop: 'number'});

///// Functions to unwrap inner types

export const listObjectType = (type: Type): Type => {
  type = nullableTaggableValue(type);
  if (isUnion(type)) {
    return union(type.members.map(m => listObjectType(m)));
  }
  if (!isList(type)) {
    throw new Error('invalid');
  }
  return type.objectType;
};

export const listLength = (type: Type): number | undefined => {
  type = nullableTaggableValue(type);
  if (!isList(type)) {
    throw new Error('invalid');
  }
  // List length is only "defined" if the max and min are equal and non-null
  if (type.maxLength != null && type.maxLength === type.minLength) {
    return type.maxLength;
  }
  return undefined;
};

export const listMaxLength = (type: Type): number | undefined => {
  type = nullableTaggableValue(type);
  if (!isList(type)) {
    throw new Error('invalid');
  }
  return type.maxLength;
};

export const listMinLength = (type: Type): number | undefined => {
  type = nullableTaggableValue(type);
  if (!isList(type)) {
    throw new Error('invalid');
  }
  return type.minLength;
};

export const taggableValue = (type: Type): Type => {
  if (isTaggedValueLike(type)) {
    type = taggedValueValueType(type);
  }
  if (isUnion(type) && type.members.every(isTaggedValue)) {
    type = union(type.members.map(m => (m as TaggedValueType).value));
  }
  return type;
};

export const nullableTaggableValue = (type: Type): Type => {
  return nonNullable(taggableValue(nonNullable(type)));
};

export const typedDictPropertyTypes = (type: Type): {[key: string]: Type} => {
  type = nullableTaggableValue(type);
  if (isUnion(type)) {
    // TODO: (tim) make this nicer code. This makes a union out of the "union" of keys
    return type.members.reduce<{[key: string]: any}>((prev, curr, ndx) => {
      const unwrappedCurr = nullableTaggableValue(curr);
      if (!isTypedDict(unwrappedCurr) && unwrappedCurr !== 'none') {
        throw new Error('invalid');
      }
      let newVal: {[key: string]: any} = {};
      if (ndx === 0) {
        newVal =
          unwrappedCurr !== 'none' ? unwrappedCurr.propertyTypes ?? {} : {};
      } else {
        for (const existingProp in prev) {
          if (prev[existingProp] != null) {
            const prevProp = prev[existingProp];
            if (
              unwrappedCurr !== 'none' &&
              unwrappedCurr.propertyTypes[existingProp]
            ) {
              const currProp = unwrappedCurr.propertyTypes[existingProp];
              if (currProp) {
                newVal[existingProp] = union([prevProp, currProp]);
              } else {
                newVal[existingProp] = union([prevProp, 'none']);
              }
            } else {
              newVal[existingProp] = union([prevProp, 'none']);
            }
          } else {
            newVal[existingProp] = prev[existingProp];
          }
        }

        if (unwrappedCurr !== 'none') {
          for (const newProp in unwrappedCurr.propertyTypes) {
            if (unwrappedCurr.propertyTypes[newProp]) {
              const currProp = unwrappedCurr.propertyTypes[newProp];
              if (currProp != null && prev[newProp] == null) {
                newVal[newProp] = union([currProp, 'none']);
              }
            }
          }
        }
      }
      return newVal;
    }, {});
  }
  if (!isTypedDict(type)) {
    throw new Error('invalid');
  }
  return type.propertyTypes as {[key: string]: Type};
};

// Files related stuff
export interface ObjectId {
  entityName: string;
  projectName: string;
  artifactTypeName: string;
  artifactSequenceName: string;
  artifactCommitHash: string;
}

const filePrefixToMediaType = (prefix: string): MediaType | null => {
  if (prefix === 'image-file') {
    return {type: 'image-file'};
  } else if (prefix === 'video-file') {
    return {type: 'video-file'};
  } else if (prefix === 'audio-file') {
    return {type: 'audio-file'};
  } else if (prefix === 'html-file') {
    return {type: 'html-file'};
  } else if (prefix === 'bokeh-file') {
    return {type: 'bokeh-file'};
  } else if (prefix === 'object3D-file') {
    return {type: 'object3D-file'};
  } else if (prefix === 'molecule-file') {
    return {type: 'molecule-file'};
  } else if (prefix === 'table') {
    return {type: 'table', columnTypes: {}};
  } else if (prefix === 'joined-table') {
    return {type: 'joined-table', columnTypes: {}};
  } else if (prefix === 'partitioned-table') {
    return {type: 'partitioned-table', columnTypes: {}};
  }
  return null;
};

export const filePathToType = (path: string) => {
  const filePath = path;
  const fileType: File = {
    type: 'file' as const,
    extension: fileExtension(filePath),
  };
  let chromePatchMatch = path.match(/.+\.(trace)(.json)?(?:.gz|.zip)?$/);
  if (chromePatchMatch != null) {
    chromePatchMatch = chromePatchMatch.slice(1);
    fileType.extension = chromePatchMatch.join('');
  } else if (filePath.endsWith('.json')) {
    const components = filePath.split('.');
    if (components.length >= 3) {
      const potentialWbObjectType = components[components.length - 2];
      const wbObjectType = filePrefixToMediaType(potentialWbObjectType);
      if (wbObjectType != null) {
        fileType.wbObjectType = wbObjectType;
      }
    }
  }
  return fileType;
};

function getIdAndPathFromURI(url: string): {id: string; path: string} {
  if (url.startsWith('//')) {
    url = url.slice(2);
  } else if (url.startsWith('/')) {
    url = url.slice(1);
  } else {
    throw new Error('Invalid artifact reference ' + url);
  }
  const [artId, refAssetPath] = splitOnce(url, '/');
  if (refAssetPath == null) {
    throw new Error('Invalid artifact reference ' + url);
  }
  return {id: artId, path: refAssetPath};
}

const WANDB_ARTIFACT_SCHEME = 'wandb-artifact:';
const WANDB_ARTIFACT_CLIENT_SCHEME = 'wandb-client-artifact:';
export function parseArtifactRef(ref: string) {
  if (ref.startsWith(WANDB_ARTIFACT_SCHEME)) {
    const {id, path} = getIdAndPathFromURI(
      ref.slice(WANDB_ARTIFACT_SCHEME.length)
    );
    return {artifactId: hexToId(id), assetPath: path};
  } else if (ref.startsWith(WANDB_ARTIFACT_CLIENT_SCHEME)) {
    const {id, path} = getIdAndPathFromURI(
      ref.slice(WANDB_ARTIFACT_CLIENT_SCHEME.length)
    );
    return {artifactId: id, assetPath: path};
  }
  throw new Error('invalid artifact reference: ' + ref);
}

export interface ReadyManifest {
  manifest: Manifest;
  layout: 'V1' | 'V2';
  rootMetadata: DirMetadata;
}

export interface FileEntry {
  size: number;
  ref?: string;
  digest?: string;
  birthArtifactID?: string;
}

export interface Manifest {
  storagePolicy: string;
  storagePolicyConfig: {[key: string]: any};
  contents: {[name: string]: FileEntry};
}

export type FullFilePath = ObjectId & {
  path: string;
};

export interface FileDesc {
  fullPath: FullFilePath | null;
  loading: boolean;
  contents: string | null;
}

export interface FileDirectUrl {
  fullPath: FullFilePath;
  refPath: ObjectId | null;
  loading: boolean;
  directUrl: string | null;
}
export interface DirMetadata {
  type: 'dir';
  fullPath: string;
  size: number;

  dirs: {[name: string]: DirMetadata};
  files: {[name: string]: FileMetadata};
}

export type FileMetadata = {
  type: 'file';
  fullPath: string;
  url: string;
} & FileEntry;
export type MetadataNode = DirMetadata | FileMetadata;

export interface FilePathWithMetadata {
  path: FullFilePath;
  metadata: MetadataNode | null;
}

export interface PathMetadata {
  fullPath: FullFilePath;
  loading: boolean;
  node: MetadataNode | null;
}

export interface FilePathMetadata {
  fullPath: FullFilePath;
  loading: boolean;
  node: FileMetadata;
}

export interface LoadedPathMetadata {
  _type: 'loaded-path';
  fullPath: FullFilePath;
  node: MetadataNode | null;
}

export const isLoadedPathMetadata = (o: any): o is LoadedPathMetadata => {
  return o._type === 'loaded-path';
};

export interface ArtifactFileId {
  artifactId: string;
  path: string;
}

export interface ArtifactFileContent {
  refFileId: ArtifactFileId | null;
  contents: string | null;
}

export interface ArtifactFileDirectUrl {
  refFileId: ArtifactFileId | null;
  directUrl: string | null;
}

export interface RunPathInfo {
  entityName: string;
  projectName: string;
  runId: string;
}

export type RunFileIdWithPathInfo = RunFileId & RunPathInfo;

export interface RunFileId {
  runId: string;
  path: string;
}

export interface RunFileContent {
  contents: string | null;
}

export interface RunFileDirectUrl {
  directUrl: string | null;
}
